// What the customer can put into the Management Report.
//
// The layout is fixed — two chart pages and a table page — and the customer
// chooses what goes in each slot, or leaves it out. An option is offered only
// when the feature behind it is enabled for BOTH the user and the channel,
// which is the same rule the sidebar uses to decide what a customer may see.

export const NONE = "none";

// requires: sidebar key the option depends on, or null when it needs no permission.
export const CHART_OPTIONS = [
  // Both of the first two are "most watched"; the qualifier says which figure
  // was used, because the two rank the programmes differently.
  { value: "topWatched", label: "En çok izlenen 10 program (Total TR)", requires: null },
  {
    value: "topActiveTv",
    label: "En çok izlenen 10 program (Ort. Aktif TV)",
    requires: "avgactivetv",
  },
  { value: "topSwitchIn", label: "En çok switch-in alan 10 program", requires: "switchin" },
];

export const TABLE_COLUMNS = [
  { value: "viewers", label: "İzlenme", requires: null },
  { value: "switchIn", label: "Switch-in", requires: "switchin" },
  { value: "avgActiveTv", label: "Ort. Aktif TV", requires: "avgactivetv" },
  { value: "twitter", label: "Twitter", requires: "socialmediacompare" },
  { value: "youtube", label: "YouTube", requires: "socialmediacompare" },
];

export const TOTALS_OPTIONS = [
  { value: "daily", label: "Günlük toplamlar", requires: null },
  { value: "monthly", label: "Aylık toplamlar", requires: null },
];

// Which table columns also carry a figure in the totals block. Each is taken
// from the day's own series rather than by adding up the table rows, so an
// average stays an average and shared minutes are not counted twice.
//
// With every one of these switched off there is nothing to total, and the
// period checkboxes have nothing to print.
export const TOTALLED_COLUMNS = [
  "viewers",
  "switchIn",
  "avgActiveTv",
  "twitter",
  "youtube",
];

export function hasTotals(config) {
  return (config.tableColumns || []).some((c) => TOTALLED_COLUMNS.includes(c));
}


export const VALUE_OPTIONS = [
  {
    value: "avgVideoConsumption",
    label: "Ort. kanalda kalma (dk)",
    requires: "avgvidconsumption",
  },
];

// A feature is available only when the user is allowed it AND the channel
// supports it — the intersection ChannelSidebar applies.
export function allowedFeatures(userSidebarItems = [], channelSidebarItems = []) {
  const user = new Set(userSidebarItems.map((s) => String(s).toLowerCase()));
  return new Set(
    channelSidebarItems
      .map((s) => String(s).toLowerCase())
      .filter((s) => user.has(s))
  );
}

function permitted(option, features) {
  return !option.requires || features.has(option.requires);
}

export function availableChartOptions(features) {
  return CHART_OPTIONS.filter((o) => permitted(o, features));
}

export function availableTableColumns(features) {
  return TABLE_COLUMNS.filter((o) => permitted(o, features));
}

export function availableTotals(features) {
  return TOTALS_OPTIONS.filter((o) => permitted(o, features));
}

export function availableValues(features) {
  return VALUE_OPTIONS.filter((o) => permitted(o, features));
}

// Everything the customer is entitled to, switched on. They subtract from here.
export function defaultReportConfig(features) {
  const charts = availableChartOptions(features);
  return {
    chart1: charts[0]?.value ?? NONE,
    chart2: charts[1]?.value ?? NONE,
    tableEnabled: true,
    tableColumns: availableTableColumns(features).map((c) => c.value),
    totals: availableTotals(features).map((t) => t.value),
    values: availableValues(features).map((v) => v.value),
  };
}

// True when the report would come out empty, so the export button can say so
// instead of producing a blank PDF.
export function isReportEmpty(config) {
  return (
    config.chart1 === NONE &&
    config.chart2 === NONE &&
    !config.tableEnabled &&
    config.totals.length === 0 &&
    (config.values || []).length === 0
  );
}
